import React from 'react';
import { CrownIcon, CrossedLimitIcon, ImprovedIcon, NotImprovedIcon, ZeroIcon } from 'assests/GmsIcons';
import Typography from 'Components/DesignSystem/Typography/Typography';

/**
 * What the glyphs in the results table's Result column mean (§ 165).
 *
 * **Replaces `TooltipIcons`** — a single 141×147 `<svg>` with the whole legend baked
 * into it, five icons and five words, the words as vector paths filled `#090A42`. Two
 * problems with that, and the second is the one that shows:
 *
 * - It was **one unlabelled `<svg>`**, so a screen reader got nothing at all from the
 *   only place the app explains its own result vocabulary.
 * - `#090A42` is near-black, and since § 50 the tooltip bubble is `surface-inverse`
 *   (#172b4d). Baked-in ink cannot follow a themed surface: the legend was roughly
 *   **1.2:1** against the ground it now sits on.
 *
 * As real markup it inherits `ink-inverse` from the bubble, reads in both themes, and is
 * announced. The five icons are the same components `ResultStatus` uses, so the legend
 * and the table can no longer drift apart.
 *
 * `TooltipIcons` itself stays in `GmsIcons.tsx` — it is exported and this is not the
 * change that audits every icon export.
 */
export const RESULT_LEGEND: Array<{ id: string; icon: React.ReactNode; label: string }> = [
    { id: 'won', icon: <CrownIcon />, label: 'Winner' },
    { id: 'crossed_limit', icon: <CrossedLimitIcon />, label: 'Crossed MLP' },
    { id: 'improved', icon: <ImprovedIcon />, label: 'Improvement' },
    { id: 'not_improved', icon: <NotImprovedIcon />, label: 'Degradation' },
    { id: 'zero', icon: <ZeroIcon />, label: 'No change' },
];

const ResultLegend = () => (
    <ul className="tw-flex tw-flex-col tw-gap-1.5 tw-m-0 tw-p-0 tw-list-none" data-testid="ab-result-legend">
        {RESULT_LEGEND.map((entry) => (
            <li key={entry.id} className="tw-flex tw-items-center tw-gap-2">
                {/* Decorative: the word beside it is the label. */}
                <span aria-hidden="true" className="tw-flex tw-items-center tw-shrink-0">{entry.icon}</span>
                <Typography variant="caption-metadata" as="span" className="tw-whitespace-nowrap">
                    {entry.label}
                </Typography>
            </li>
        ))}
    </ul>
);

export default ResultLegend;
